import { Table } from "../../../../../../shared/app/Table";
import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import {
  getCoreRowModel,
  RowSelectionState,
  useReactTable
} from "@tanstack/react-table";
import moment from "moment/moment";
import {
  Action,
  ContextMenuPopover
} from "../../../../../../shared/components/ContextMenuPopover";
import { Download01, Printer, Trash02 } from "untitledui-js-base";
import { Checkbox } from "shared/components";
import { DeleteDocumentModal } from "./DeleteDocumentModal";
import { DocTypeLabelMap, MAX_FILE_NAME_LENGTH } from "../../constants";
import { getFullName } from "shared/utils/get-full-name";
import { AdditionalDocument } from "../../types";

interface Props {
  documents: AdditionalDocument[];
  selectedRentersRow: RowSelectionState;
  onDelete: (docId: number) => void;
  onDownload: (files: { url: string; name: string }[]) => void;
  onPrint: (url: string) => void;
  setSelectedRentersRow: Dispatch<SetStateAction<RowSelectionState>>;
}

const shortenFileName = (fileName: string) => {
  if (!fileName) return "";
  const [name, ext] = fileName.split(/\.(?=[^.]+$)/);
  return name.length > MAX_FILE_NAME_LENGTH
    ? `${name.slice(0, MAX_FILE_NAME_LENGTH)}...${ext}`
    : fileName;
};

export const DocumentTable: FC<Props> = ({
  documents,
  selectedRentersRow,
  onDelete,
  onDownload,
  onPrint,
  setSelectedRentersRow
}) => {
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);

  const columns = useMemo(
    () => [
      {
        id: "checkbox custom_ fixed_width",
        header: ({ table }) => (
          <Checkbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler()
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler()
            }}
          />
        ),
        size: 32
      },
      {
        id: "file_name custom_",
        accessorFn: (row) => row,
        header: "Name",
        size: 100,
        cell: (p) => {
          const { asset_name, document_type } = p.getValue();
          return (
            <div className="space-y-1">
              <p className="text-sm font-medium text-content-1000">
                {shortenFileName(asset_name)}
              </p>
              <p className="text-sm text-content-400">
                {DocTypeLabelMap[document_type]}
              </p>
            </div>
          );
        }
      },
      {
        id: "uploaded_by custom_",
        accessorFn: (row) => row,
        header: () => <p className="pl-4">Uploaded by</p>,
        size: 80,
        cell: (p) => {
          const uploadedBy = p.getValue().uploaded_by;
          if (!uploadedBy) return null;
          const { role, first_name, middle_name, last_name, name, email } =
            uploadedBy;
          const isTeamBoom = email?.endsWith("@boompay.app");
          return (
            <div className="space-y-1 pl-4">
              <p className="text-sm font-medium text-content-1000 capitalize">
                {isTeamBoom ? "Team Boom" : role}
              </p>
              {!isTeamBoom ? (
                <p className="text-xs text-content-400">
                  {name ??
                    getFullName({
                      firstName: first_name,
                      lastName: last_name,
                      middleName: middle_name,
                      short: true
                    })}
                </p>
              ) : null}
            </div>
          );
        }
      },
      {
        id: "uploaded_at custom_",
        accessorKey: "created_at",
        header: () => <p className="pl-4">Uploaded at</p>,
        size: 80,
        cell: (p) => (
          <p className="text-content-1000 text-xs pl-4">
            {moment(p.getValue()).format("MMM D, YYYY [at] h:mm A")}
          </p>
        )
      },
      {
        id: "popover-action align-right fixed_width",
        accessorFn: (row) => row,
        header: "",
        size: 60,
        cell: (p) => {
          const { asset_url, asset_name, id } = p.getValue();

          const popoverActions: Action[] = [
            {
              title: (
                <span className="flex gap-2 items-center">
                  <Download01 size="18" className="stroke-content-200" />
                  Download
                </span>
              ),
              onClick: () => onDownload([{ url: asset_url, name: asset_name }])
            },
            {
              title: (
                <span className="flex gap-2 items-center">
                  <Printer size="18" className="stroke-content-200" />
                  Print
                </span>
              ),
              onClick: () => {
                onPrint(asset_url);
              }
            },
            {
              title: (
                <span className="flex gap-2 items-center">
                  <Trash02 size="18" className="stroke-content-200" />
                  Delete
                </span>
              ),
              onClick: () => {
                setDeleteModalId(id);
              }
            }
          ];

          return (
            <ContextMenuPopover
              triggerClassName="border border-alpha-100"
              actions={popoverActions}
            />
          );
        }
      }
    ],
    []
  );

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection: selectedRentersRow
    },
    enableRowSelection: true,
    onRowSelectionChange: setSelectedRentersRow,
    getRowId: (row) => String(row.id)
  });

  return (
    <>
      <Table thClassName="!p-0" borderLess rowHeight="76px" table={table} />
      <DeleteDocumentModal
        opened={Boolean(deleteModalId)}
        onClose={() => setDeleteModalId(null)}
        onDelete={() => onDelete(deleteModalId!)}
      />
    </>
  );
};
